import React from 'react';
import { Link } from 'react-router-dom';
import { CloudArrowUpIcon, ServerStackIcon, LockClosedIcon, ArrowRightIcon, AcademicCapIcon, CogIcon, ShieldCheckIcon, BoltIcon, CircleStackIcon, CommandLineIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import techImage from '../img/tech1.jpg';

// Consistent PageHeader for detail pages
const PageHeader = ({ title, subtitle, breadcrumbs }) => (
  <div className="bg-figma-dark-bg-secondary py-16 md:py-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      {breadcrumbs && (
        <nav className="text-sm mb-4 animate-fade-in-up">
          {breadcrumbs.map((crumb, index) => (
            <span key={index} className="text-figma-dark-text-secondary/80">
              {index > 0 && <span className="mx-2">/</span>}
              {crumb.to ? (
                <Link to={crumb.to} className="hover:text-figma-dark-primary transition-colors">{crumb.label}</Link>
              ) : (
                <span>{crumb.label}</span>
              )}
            </span>
          ))}
        </nav>
      )}
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-figma-dark-text-primary animate-fade-in-up" style={{animationDelay: '0.1s'}}>{title}</h1>
      {subtitle && <p className="mt-4 text-lg text-figma-dark-text-secondary max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: '0.2s'}}>{subtitle}</p>}
    </div>
  </div>
);

// Card for features/capabilities - Figma Dark Style
const FeatureCard = ({ icon: Icon, title, description, animationDelay }) => (
  <div className="bg-figma-dark-bg-primary border border-figma-dark-border rounded-lg p-6 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col items-center text-center hover:animate-subtle-3d-tilt-hover animate-fade-in-up" style={{animationDelay}}>
    <Icon className="h-12 w-12 text-figma-dark-primary mb-5" />
    <h3 className="text-xl font-semibold text-figma-dark-text-primary mb-2">{title}</h3>
    <p className="text-base text-figma-dark-text-secondary leading-relaxed flex-grow">{description}</p>
  </div>
);

// Section component for consistent styling - Figma Dark Style
const ContentSection = ({ title, children, className = '' }) => (
  <section className={`py-12 md:py-16 ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {title && <h2 className="text-2xl md:text-3xl font-semibold text-figma-dark-text-primary text-center mb-10 md:mb-12 animate-fade-in-up">{title}</h2>}
      {children}
    </div>
  </section>
);

const CloudSolutionsPage = () => {
  const breadcrumbs = [
    { label: 'Home', to: '/' },
    { label: 'Services', to: '/services' }, 
    { label: 'Cloud Solutions', to: '/services/cloud-solutions' }
  ];

  const services = [
    {
      title: "Multi-Cloud Architecture",
      description: "Design and implement robust multi-cloud strategies that leverage the best features of multiple cloud providers for optimal performance and cost efficiency.",
      icon: "🌐",
      features: [
        "Hybrid Cloud Integration",
        "Cloud Provider Optimization",
        "Cross-Cloud Management",
        "Cost Optimization"
      ]
    },
    {
      title: "Serverless Computing",
      description: "Build scalable applications using serverless architecture to reduce operational overhead and improve cost efficiency.",
      icon: "⚡",
      features: [
        "Function as a Service",
        "Event-Driven Architecture",
        "Auto-scaling Solutions",
        "Pay-per-use Model"
      ]
    },
    {
      title: "Edge Computing",
      description: "Deploy and manage edge computing solutions to process data closer to the source, reducing latency and improving performance.",
      icon: "🔮",
      features: [
        "Edge Analytics",
        "IoT Integration",
        "Real-time Processing",
        "Distributed Systems"
      ]
    },
    {
      title: "Cloud-Native Development",
      description: "Develop and deploy applications using cloud-native technologies and practices for maximum scalability and reliability.",
      icon: "🚀",
      features: [
        "Container Orchestration",
        "Microservices Architecture",
        "CI/CD Pipelines",
        "Infrastructure as Code"
      ]
    }
  ];

  const benefits = [
    {
      title: "Scalability",
      description: "Scale your infrastructure up or down based on demand, ensuring optimal performance and cost efficiency."
    },
    {
      title: "Security",
      description: "Implement enterprise-grade security measures with advanced threat detection and compliance management."
    },
    {
      title: "Cost Efficiency",
      description: "Optimize your cloud spending with intelligent resource allocation and usage-based pricing models."
    },
    {
      title: "Innovation",
      description: "Leverage cutting-edge cloud technologies to drive innovation and maintain competitive advantage."
    }
  ];

  return (
    <div className="min-h-screen bg-figma-dark-bg-primary text-figma-dark-text-primary">
      {/* Hero Section */}
      <section 
        className="relative min-h-[60vh] bg-cover bg-center flex items-center justify-center"
        style={{ backgroundImage: `url(${techImage})` }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 text-center px-4 py-16">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-figma-dark-primary to-figma-dark-accent bg-clip-text text-transparent">
              Cloud Solutions
            </span>
          </h1>
          <p className="text-xl text-figma-dark-text-secondary max-w-3xl mx-auto">
            Next-generation cloud infrastructure and services for digital transformation
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              className="bg-figma-dark-bg-secondary/30 backdrop-blur-sm border border-figma-dark-border rounded-xl p-8 hover:shadow-figma-dark-glow transition-all duration-300"
            >
              <div className="text-5xl mb-6">{service.icon}</div>
              <h3 className="text-2xl font-semibold mb-4 text-white">{service.title}</h3>
              <p className="text-figma-dark-text-secondary mb-6">
                {service.description}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {service.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center space-x-2 text-sm text-figma-dark-text-secondary">
                    <span className="text-figma-dark-primary">•</span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-figma-dark-bg-secondary/20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-white">Key Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-figma-dark-bg-secondary/30 backdrop-blur-sm border border-figma-dark-border rounded-xl p-6 text-center"
              >
                <h3 className="text-xl font-semibold mb-3 text-white">{benefit.title}</h3>
                <p className="text-figma-dark-text-secondary">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6 text-white">Ready to Transform Your Cloud Infrastructure?</h2>
          <p className="text-figma-dark-text-secondary mb-8">
            Let's discuss how our cloud solutions can help optimize your business operations
          </p>
          <Link
            to="/contact"
            className="px-8 py-4 rounded-full bg-gradient-to-r from-figma-dark-primary to-figma-dark-accent text-white font-medium hover:shadow-figma-dark-glow transition-all duration-300 hover:scale-105"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CloudSolutionsPage; 